// Derives the InsightsView the AI Insights agent reads for a dashboard — the
// STRUCTURAL half: which gadgets are on screen, what metrics they show, how they
// slice them. Unlike a report, a dashboard has no view-level date range or filter
// set — each gadget carries its own — so those fields are left empty here and
// filled per-gadget at send time from the scope registry (see mergeScopeIntoView).
//
// Pure by design so the derivation is unit-testable without mounting a dashboard;
// RegisterDashboardInsightsView is the thin component that reads the contexts and
// calls this. Shares its gadget-shape logic with the report builder via
// insightsViewGadget so the two never drift.

import describeComparison from 'formatters/describeComparison';
import cardTypeCheckers from 'types/cardTypeCheckers';
import visTypeCheckers from 'types/visTypeCheckers';

import {
  InsightsView,
  InsightsViewGadget,
  viewFilterClauses,
} from 'contextProviders/InsightsContentProvider/viewTypes';
import {
  BaseViewsLookup,
  buildDimensions,
  buildDrillTarget,
  datasetsForMetric,
  describeSeriesOverrides,
  describeTrend,
  MAX_VIEW_GADGETS,
  MetricOptionsLookup,
} from 'components/InsightsChat/insightsViewGadget';

export interface DashboardInsightsViewInput {
  cards: CanvasCard.Card[];
  dashboardGadgetsLookup: { [id: string]: DashboardGadget | undefined };
  baseViews: BaseViewsLookup;
  metricOptionsLookup: MetricOptionsLookup;
  defaultDatasetFiltersLookup: {
    [dataset: string]: DefaultDatasetFilter | undefined;
  };
  // From useGetMetricIdsFromVis — reused so every gadget type resolves its
  // metrics the same way the rest of the app does.
  getMetricIds: (vis?: ExtendedVisualisationDefinition) => string[];
  // Names for the gadgets' popup links (reportDrillDownId / boardDrillDownId),
  // so the agent can say "clicking opens Lane Detail" rather than quote an id.
  reportNamesById?: { [id: string]: string | undefined };
  boardNamesById?: { [id: string]: string | undefined };
}

const buildDashboardInsightsView = ({
  cards,
  dashboardGadgetsLookup,
  baseViews,
  metricOptionsLookup,
  defaultDatasetFiltersLookup,
  getMetricIds,
  reportNamesById = {},
  boardNamesById = {},
}: DashboardInsightsViewInput): InsightsView => {
  // Reading order — top to bottom, then left to right — so truncating at
  // MAX_VIEW_GADGETS keeps what the user is most likely looking at.
  const gadgetCards = cards
    .filter(cardTypeCheckers.isDashboardGadget)
    .slice()
    .sort((a, b) =>
      a.layout.y === b.layout.y
        ? a.layout.x - b.layout.x
        : a.layout.y - b.layout.y,
    );

  // Seeded from structural signals only (unreproducible trends, invisible default
  // dataset filters). Per-gadget resolved filters that can't be reproduced are
  // OR-ed in at send time by mergeScopeIntoView.
  let hasUnreproducibleFilters = false;

  const allGadgets: InsightsViewGadget[] = [];
  gadgetCards.forEach((card) => {
    const gadget = dashboardGadgetsLookup[card.content.dashboardGadgetId];
    // A gadget saved without a chart (e.g. an abandoned add) draws nothing, so
    // there's nothing on screen to describe
    if (!gadget || !gadget.chartDef) {
      return;
    }
    const { chartDef } = gadget;

    const metricIds = [...new Set(getMetricIds(chartDef))];
    const metrics = metricIds.map((id) => {
      const name = metricOptionsLookup[id]?.name;
      return { id, ...(name ? { name } : {}) };
    });

    // Default dataset filters are applied invisibly per-gadget and have no
    // aggregate_metric equivalent, so their presence alone means a recomputed
    // figure may legitimately differ from the screen.
    if (!gadget.isDefaultFiltersDisabled) {
      const hasDefaultFilter = metricIds
        .flatMap((id) =>
          datasetsForMetric({ metricId: id, metricOptionsLookup }),
        )
        .some((dataset) => !!defaultDatasetFiltersLookup[dataset]);
      if (hasDefaultFilter) {
        hasUnreproducibleFilters = true;
      }
    }

    const isV5 = visTypeCheckers.isV5ChartDef(chartDef);
    const trend = isV5 ? describeTrend(chartDef) : {};
    if (trend.isTrendReproducible === false) {
      hasUnreproducibleFilters = true;
    }

    const dimensions = buildDimensions({ chartDef, baseViews });

    // Series asking their own question, which a re-query must ask too
    const seriesOverrides = describeSeriesOverrides({
      chartDef,
      metricOptionsLookup,
      baseViews,
    });
    if (
      seriesOverrides.some((series) =>
        (series.filters ?? []).some((f) => viewFilterClauses(f).length === 0),
      )
    ) {
      hasUnreproducibleFilters = true;
    }

    // Only dashboard gadgets carry a popup link — on a report a click filters
    // the page itself, so the report builder never sets this.
    const drillTarget = buildDrillTarget({
      reportDrillDownId: gadget.reportDrillDownId,
      boardDrillDownId: gadget.boardDrillDownId,
      reportNamesById,
      boardNamesById,
    });

    allGadgets.push({
      // The card id, not the gadget id: the scope/values registries are keyed by
      // card, so the same gadget placed twice reports two sets of numbers.
      cardId: card.layout.i,
      title: chartDef.name,
      metrics,
      ...dimensions,
      ...trend,
      ...(isV5 && chartDef.groupByLimit !== undefined
        ? { groupByLimit: chartDef.groupByLimit }
        : {}),
      ...(isV5 && chartDef.groupBySortBy
        ? { groupBySort: chartDef.groupBySortBy }
        : {}),
      // A gadget's OWN date/filter scope is absolute on a dashboard, not a delta,
      // so it is NOT reported via dateFieldOverride/extraFilters/ignoresReport
      // (those describe how a REPORT gadget deviates from the report). It arrives
      // as `ownScope`, filled at send time from the per-gadget scope registry.
      ...(gadget.comparison
        ? { comparison: describeComparison(gadget.comparison) }
        : {}),
      ...(seriesOverrides.length > 0 ? { seriesOverrides } : {}),
      ...(drillTarget ? { drillTarget } : {}),
    });
  });

  const gadgets = allGadgets.slice(0, MAX_VIEW_GADGETS);

  // A dashboard has no view-level date range or filter set — each gadget carries
  // its own (see ownScope) — so the top-level fields stay empty. `filters` is
  // required by the type; an empty array is the honest value.
  return {
    filters: [],
    hasUnreproducibleFilters,
    gadgets,
    ...(allGadgets.length > gadgets.length
      ? { gadgetsTruncatedFrom: allGadgets.length }
      : {}),
  };
};

export default buildDashboardInsightsView;
